import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { createRjPoster, updateRjPoster, deleteRjPoster } from '../redux/actions/rjActions';
import { clearRjState } from '../redux/reducers/rjSlice';
import Toast from '../common/toast-message/Toast';
import useToast from '../common/toast-message/useToast';

const Rj = () => {
  const dispatch = useDispatch();
  const { adminData } = useSelector((state) => state.admin);
  const { 
    loading: rjLoading, 
    error: rjError, 
    success: rjSuccess,
    updateLoading: rjUpdateLoading,
    updateError: rjUpdateError,
    updateSuccess: rjUpdateSuccess,
    deleteLoading: rjDeleteLoading,
    deleteError: rjDeleteError,
    deleteSuccess: rjDeleteSuccess
  } = useSelector((state) => state.rj);

  const [rjs, setRjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBio, setExpandedBio] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteRjPending, setDeleteRjPending] = useState(null);
  const { toastMessage, showToastMessage, hideToast } = useToast();

  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingRj, setEditingRj] = useState(null);
  const [deletingRjId, setDeletingRjId] = useState(null);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const totalEntries = rjs.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentRjs = rjs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedBio(null);
  };

  const [rjData, setRjData] = useState({
    rjName: '',
    rjProgramName: '',
    rjBio: '',
    rjPoster: ''
  });
  
  const [imagePreview, setImagePreview] = useState('');

const fetchRjs = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    
    const url = `${process.env.REACT_APP_API_BASE_USER_URL}/isGuest/rjPosters-list`;
    console.log('Fetching RJs from:', url);
    
    const response = await axios.get(url);
    
    // Get the array of RJs
    const rjArray = Array.isArray(response.data) ? response.data : [];
    console.log('Total RJs found:', rjArray.length);
    
    // SHOW ALL RJS (no filter)
    setRjs(rjArray);
    
  } catch (err) {
    setError(err.message);
    console.error('Error fetching RJs:', err);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchRjs();
  }, [fetchRjs]);

  useEffect(() => {
    if (rjSuccess) {
      showToastMessage('RJ Created Successfully!');
    }
    if (rjUpdateSuccess) {
      showToastMessage('RJ Updated Successfully!');
    }
    if (rjDeleteSuccess) {
      showToastMessage('RJ Deleted Successfully!');
    }
    
    if (rjSuccess || rjUpdateSuccess || rjDeleteSuccess) {
      fetchRjs();
    }
  }, [rjSuccess, rjUpdateSuccess, rjDeleteSuccess, fetchRjs, showToastMessage]);

  useEffect(() => {
    if (rjSuccess || rjUpdateSuccess || rjDeleteSuccess) {
      fetchRjs();
      if (rjSuccess) {
        setShowCreatePopup(false);
        setRjData({ 
          rjName: '', 
          rjProgramName: '', 
          rjBio: '', 
          rjPoster: ''
        });
        setImagePreview('');
      }
      if (rjUpdateSuccess) {
        setShowEditPopup(false);
        setEditingRj(null);
        setRjData({ 
          rjName: '', 
          rjProgramName: '', 
          rjBio: '', 
          rjPoster: ''
        });
        setImagePreview('');
      }
      if (rjDeleteSuccess) {
        setDeletingRjId(null);
      }
      
      const timer = setTimeout(() => {
        dispatch(clearRjState());
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [rjSuccess, rjUpdateSuccess, rjDeleteSuccess, dispatch, fetchRjs]);

  const extractRjPosterId = (rj) => {
    if (rj.rjPosterId) return rj.rjPosterId;
    if (rj.PK && rj.PK.startsWith('RJPOSTER#')) {
      return rj.PK.replace('RJPOSTER#', '');
    }
    return rj.PK || 'unknown-id';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size too large. Please choose an image smaller than 5MB.');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      try {
        const base64Data = await fileToBase64(file);
        setRjData({ 
          ...rjData, 
          rjPoster: base64Data
        });
      } catch (error) {
        alert('Error processing image: ' + error.message);
      }
    }
  };

  const handleCreateRj = () => {
    if (!adminData) {
      alert('Admin data not available');
      return;
    }

    if (!rjData.rjName.trim() || 
        !rjData.rjProgramName.trim() || 
        !rjData.rjBio.trim() || 
        !rjData.rjPoster) {
      alert('Please fill in all required fields: RJ Name, Program Name, Bio, and upload an image');
      return;
    }

    const payload = {
      id: adminData.id,
      rjName: rjData.rjName,
      rjProgramName: rjData.rjProgramName,
      rjBio: rjData.rjBio,
      rjPoster: rjData.rjPoster,
    };

    console.log('Creating RJ with payload:', {
      ...payload,
      rjPoster: `${rjData.rjPoster.substring(0, 50)}...`
    });
    
    dispatch(createRjPoster(adminData, payload));
  };

  const handleUpdateRj = () => {
    if (!adminData || !editingRj) {
      alert('Admin data or RJ data not available');
      return;
    }

    if (!rjData.rjName.trim() || 
        !rjData.rjProgramName.trim() || 
        !rjData.rjBio.trim()) {
      alert('Please fill in RJ Name, Program Name, and Bio');
      return;
    }

    const rjPosterId = extractRjPosterId(editingRj);
    
    const payload = {
      id: adminData.id,
      rjPosterId: rjPosterId,
      rjName: rjData.rjName,
      rjProgramName: rjData.rjProgramName,
      rjBio: rjData.rjBio
    };

    if (rjData.rjPoster && rjData.rjPoster.startsWith('data:')) {
      const base64Data = rjData.rjPoster.split(',')[1];
      payload.rjPoster = base64Data;
    } else if (rjData.rjPoster && rjData.rjPoster.length > 100) {
      payload.rjPoster = rjData.rjPoster;
    }

    console.log('Updating RJ with payload:', {
      ...payload,
      rjPoster: payload.rjPoster ? `${payload.rjPoster.substring(0, 50)}...` : 'using existing'
    });
    
    dispatch(updateRjPoster(adminData, payload));
  };

  const handleDeleteRj = async (rj) => {
    const rjPosterId = extractRjPosterId(rj);
    setDeletingRjId(rjPosterId);

    try {
      await dispatch(deleteRjPoster(adminData, rjPosterId));
      showToastMessage('RJ deleted successfully!');
    } catch (error) {
      showToastMessage('Failed to delete RJ: ' + error.message);
    }
    setDeletingRjId(null);
  };

  const openEditPopup = (rj) => {
    console.log('Editing RJ:', rj);
    setEditingRj(rj);
    setRjData({
      rjName: rj.rjName || '',
      rjProgramName: rj.rjProgramName || '',
      rjBio: rj.rjBio || '',
      rjPoster: ''
    });
    setShowEditPopup(true);
  };

  const handleOpenCreatePopup = () => {
    dispatch(clearRjState());
    setShowCreatePopup(true);
    setRjData({ 
      rjName: '', 
      rjProgramName: '', 
      rjBio: '', 
      rjPoster: ''
    });
  };

  const handleCloseCreatePopup = () => {
    dispatch(clearRjState());
    setShowCreatePopup(false);
    setRjData({ 
      rjName: '', 
      rjProgramName: '', 
      rjBio: '', 
      rjPoster: ''
    });
  };

  const handleCloseEditPopup = () => {
    dispatch(clearRjState());
    setShowEditPopup(false);
    setEditingRj(null);
    setRjData({ 
      rjName: '', 
      rjProgramName: '', 
      rjBio: '', 
      rjPoster: ''
    });
  };

  const getRjImageUrl = (rj) => {
    return rj.thumbnail || rj.rjPoster || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <svg className="animate-spin h-12 w-12 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-96 flex-col">
        <div className="text-red-500 text-lg mb-4">Error: {error}</div>
        <button 
          onClick={fetchRjs}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-full mx-auto px-2 sm:px-4">
        <div className="flex justify-between items-center mb-3 sm:mb-4">
          <h1 className="text-lg sm:text-2xl font-semibold text-gray-800">RJs</h1>
          <button
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            onClick={handleOpenCreatePopup}
          >
            <span className="hidden sm:inline">+ Create RJ</span>
            <span className="sm:hidden">+ Create</span>
          </button>
        </div>

        {rjDeleteSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-xs sm:text-sm">
            RJ Deleted Successfully!
          </div>
        )}

        <div className="flex items-center justify-between py-2 flex-wrap gap-2">
          <div className="text-xs sm:text-sm text-gray-600">
            Showing {totalEntries === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
          </div>
          <div className="flex items-center gap-1">
            <select
              value={entriesPerPage}
              onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${currentPage === page
                  ? 'bg-green-400 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`}
              >
                {page}
              </button>
            ))}
            {totalPages > 1 && (
              <button
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ›
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {rjs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              No RJs found at the moment
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">RJ Name</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">Program</th>
                      <th className="w-24 px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">Bio</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">Updated Date</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-600 whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentRjs.map((rj, index) => {
                      const rjId = extractRjPosterId(rj);
                      const imageUrl = getRjImageUrl(rj);
                      const isDeleting = rjDeleteLoading && deletingRjId === rjId;

                      return (
                        <React.Fragment key={rjId}>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {imageUrl ? (
                                    <img 
                                      src={`${imageUrl}?t=${rj.updatedAt || rj.createdAt || Date.now()}`}
                                      alt={rj.rjName}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <span 
                                    className={`text-white font-semibold text-xs sm:text-sm ${imageUrl ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}
                                  >
                                    {rj.rjName?.charAt(0) || 'RJ'}
                                  </span>
                                </div>
                                <span className="text-xs sm:text-sm font-medium text-gray-800 truncate max-w-[80px] sm:max-w-[200px]">
                                  {rj.rjName}
                                </span>
                              </div>
                            </td>

                            <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 truncate max-w-[80px] sm:max-w-[150px]">
                                {rj.rjProgramName || 'No Program'}
                              </span>
                            </td>

                            <td className="px-2 sm:px-4 py-2 sm:py-3">
                              <div className="max-w-[150px] sm:max-w-[200px] relative group">
                                {rj.rjBio ? (
                                  <>
                                    <span className="text-xs sm:text-sm text-gray-600 block truncate max-w-full">
                                      {rj.rjBio}
                                    </span>
                                    <div 
                                      className="absolute left-0 top-full mt-2 w-max max-w-xs p-2 text-xs text-white bg-gray-700 rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-normal z-50"
                                      style={{ whiteSpace: 'normal' }}
                                    >
                                      {rj.rjBio}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-gray-400 text-xs sm:text-sm">-</span>
                                )}
                              </div>
                            </td>

                            <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                              {rj.updatedAt ? (
                                <span className="text-xs sm:text-sm text-gray-600">
                                  {new Date(rj.updatedAt).toLocaleDateString('en-GB', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric' 
                                  }).split('/').join('-')}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs sm:text-sm">-</span>
                              )}
                            </td>

                            <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <button 
                                  onClick={() => openEditPopup(rj)}
                                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit RJ"
                                >
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button 
                                  onClick={() => { setShowDeletePopup(true); setDeleteRjPending(rj); }}
                                  disabled={isDeleting}
                                  className="p-1 sm:p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete RJ"
                                >
                                  {isDeleting ? (
                                    <svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Create RJ Popup - Image preview removed */}
        {showCreatePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-2 sm:px-0">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Create New RJ</h2>
                <button
                  onClick={handleCloseCreatePopup}
                  className="text-gray-500 hover:text-gray-700 text-lg"
                >
                  ✕
                </button>
              </div>

              {rjSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-xs sm:text-sm">
                  RJ Created Successfully!
                </div>
              )}

              {rjError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-xs sm:text-sm">
                  Error: {rjError}
                </div>
              )}

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    RJ Name *
                  </label>
                  <input
                    type="text"
                    value={rjData.rjName}
                    onChange={(e) => setRjData({ ...rjData, rjName: e.target.value })}
                    placeholder="Enter RJ name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Program Name *
                  </label>
                  <input
                    type="text"
                    value={rjData.rjProgramName}
                    onChange={(e) => setRjData({ ...rjData, rjProgramName: e.target.value })}
                    placeholder="Enter program name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Bio *
                  </label>
                  <textarea
                    value={rjData.rjBio}
                    onChange={(e) => setRjData({ ...rjData, rjBio: e.target.value })}
                    placeholder="Enter RJ bio"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjLoading}
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    RJ Image *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload RJ image (Max 5MB). Image will be converted to base64 and stored in S3.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-2 sm:space-x-3 mt-6">
                <button
                  onClick={handleCloseCreatePopup}
                  className="px-3 sm:px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 text-xs sm:text-sm"
                  disabled={rjLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRj}
                  disabled={rjLoading || !rjData.rjName.trim() || !rjData.rjProgramName.trim() || !rjData.rjBio.trim() || !rjData.rjPoster}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  {rjLoading ? 'Creating...' : 'Create RJ'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit RJ Popup - Image preview removed */}
        {showEditPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-2 sm:px-0">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Edit RJ</h2>
                <button
                  onClick={handleCloseEditPopup}
                  className="text-gray-500 hover:text-gray-700 text-lg"
                >
                  ✕
                </button>
              </div>

              {rjUpdateSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-xs sm:text-sm">
                  RJ Updated Successfully!
                </div>
              )}

              {rjUpdateError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-xs sm:text-sm">
                  Error: {rjUpdateError}
                </div>
              )}

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    RJ Name *
                  </label>
                  <input
                    type="text"
                    value={rjData.rjName}
                    onChange={(e) => setRjData({ ...rjData, rjName: e.target.value })}
                    placeholder="Enter RJ name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjUpdateLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Program Name *
                  </label>
                  <input
                    type="text"
                    value={rjData.rjProgramName}
                    onChange={(e) => setRjData({ ...rjData, rjProgramName: e.target.value })}
                    placeholder="Enter program name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjUpdateLoading}
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Bio *
                  </label>
                  <textarea
                    value={rjData.rjBio}
                    onChange={(e) => setRjData({ ...rjData, rjBio: e.target.value })}
                    placeholder="Enter RJ bio"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjUpdateLoading}
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Update RJ Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    disabled={rjUpdateLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload new image to replace existing one (Max 5MB)
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-2 sm:space-x-3 mt-6">
                <button
                  onClick={handleCloseEditPopup}
                  className="px-3 sm:px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 text-xs sm:text-sm"
                  disabled={rjUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateRj}
                  disabled={rjUpdateLoading || !rjData.rjName.trim() || !rjData.rjProgramName.trim() || !rjData.rjBio.trim()}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  {rjUpdateLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDeletePopup && deleteRjPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-2 sm:px-0">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-sm mx-auto">
            <h4 className="mb-2 text-base sm:text-lg font-semibold text-gray-900">Delete RJ</h4>
            <p className="mb-3 text-gray-700 text-sm sm:text-base">
              Are you sure you want to delete RJ&nbsp;
              <span className="font-semibold">{deleteRjPending.rjName}</span>?
            </p>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button
                className="px-3 sm:px-4 py-2 bg-gray-100 rounded-full text-gray-700 hover:bg-gray-200 text-xs sm:text-sm"
                onClick={() => { setShowDeletePopup(false); setDeleteRjPending(null); }}
              >
                Cancel
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-60 text-xs sm:text-sm"
                onClick={async () => {
                  await handleDeleteRj(deleteRjPending);
                  setShowDeletePopup(false);
                  setDeleteRjPending(null);
                }}
                disabled={rjDeleteLoading}
              >
                {rjDeleteLoading && deletingRjId === extractRjPosterId(deleteRjPending) 
                  ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toastMessage} onClose={hideToast} />
    </div>
  );
};

export default Rj;